import React from "react";
import styles from "./promotedContent.module.scss";
import cx from "classnames";
import { Media } from "../../../../types/media";
import Button from "src/components/button";
import _ from "lodash";
import { Asset } from "src/types/assets";
import { Stream } from "src/types/streams";
import { Collection } from "src/types/collections";
import Thumbnail from "src/components/thumbnail";

const DefaultAssetIcon = '/images/defaultIcons/default_asset.jpg'
const DefaultBackground = '/images/defaultIcons/DefaultBackground.jpeg'
const DefaultSimulationBackground = '/images/assets/simulationThumbnail.jpg'
const DefaultPodcastBackground = '/images/assets/podcastThumbnail.jpg'
const DefaultCourseBackground = '/images/assets/courseThumbnail.jpg'

function getDefaultCoachingBackground(entityType?: string) {
  if (entityType === "simulation") {
    return DefaultSimulationBackground;
  }

  if (entityType === "podcast") {
    return DefaultPodcastBackground;
  }

  if (entityType === "course") {
    return DefaultCourseBackground;
  }

  return DefaultAssetIcon;
}

const MediaRenderer = ({ icon }: { icon?: Media }) => {
  if (!icon) return null;

  const { type, full, thumbnail } = icon;

  if (type === "image") {
    return <img src={full} className={styles.thumbnail} alt="stream icon" />;
  }

  if (type === "video") {
    return (
      <video
        autoPlay
        playsInline
        muted
        loop
        key={thumbnail}
        className={styles.thumbnail}
      >
        <source src={full} type="video/mp4" />
      </video>
    );
  }

  return null;
};

interface PromotedContentCardProps {
  id: string;
  icon: Media;
  name?: string;
  description?: string;
  item?: Asset | Collection | Stream;
}

// Main contentCard component
export function PromotedContentCard({
  icon,
  name,
  description,
  item
}: PromotedContentCardProps) {

  var parsedDescription = description || "";
  const isCollection = item?.entityType === "collection";
  if(isCollection){
    const placeholders = {
      '{{collection.name}}': _.get(item,"name"),
      '{{collection.assets}}': _.toNumber(_.get(item, "sections")) > 0  ? "" : _.get(item, "assets") ,
      '{{contact.firstname}}': '',
      '{{contact.lastname}}': '',
      '{{contact.company}}': ''
    };


    for (const placeholder in placeholders) {
      const value = placeholders[placeholder];
      parsedDescription = parsedDescription.split(placeholder).join(value);
    }
  }

  const contentIcon: Media = icon || {
    id: "",
    type: 'image',
    full: isCollection ? DefaultBackground : DefaultAssetIcon,
    thumbnail: isCollection ? DefaultBackground : DefaultAssetIcon
  };
 
  return (
    <div className={styles.content}>
      <div className={styles.contentIcon}>
        <MediaRenderer icon={contentIcon} />
      </div>
      <div className={styles.contentDetails}>
        <p className={styles.contentName}>{name}</p>
        {_.size(parsedDescription) > 0 && <p className={styles.contentDescription}>
          {parsedDescription
            ? parsedDescription.replace(/<\/?[^>]+(>|$)/g, "").replace(/&nbsp;/g, " ")
            : ""}
        </p>} 
        <Button
          text="explore"
          variant="tertiary"
          className={styles.button}
          icon="paperflite-right-arrow"
        />
  
      </div>
    </div>
  );
}

const CoachingMediaRenderer = ({ icon, item }: { icon?: Media; item?: any }) => {
  const course = _.get(item, "course", null);
  const bannerMedia = _.get(course, "banner.background") || icon;

  if (!bannerMedia) return null;

  const { full, thumbnail } = bannerMedia;
  const entityType = _.get(item, "courseType", "").toLowerCase();
  const callType = _.get(course, "callType") || _.get(item, "callType");
  const position = _.get(course, "banner.position", "center");
  const crop = _.get(course, "banner.crop", null);
  const defaultBackground = getDefaultCoachingBackground(entityType);
  const backgroundSource = thumbnail || full || defaultBackground;

  const getCoachingMediaClassName = (entityType?: string, callType?: string): string => {
    const isSimulation = entityType === "simulation";
  
    return cx(styles.thumbnail, styles.coachingMedia, {
      [styles.course]: entityType === "course",
      [styles.podcast]: entityType === "podcast",
      [styles.simulation]: isSimulation,
      [styles.discovery]: isSimulation && callType === "discovery",
      [styles.coldcall]: isSimulation && callType === "cold_call",
      [styles.elevatorPitch]: isSimulation && callType === "elevator_pitch",
    });
  };

  if (entityType === "simulation") {
    return (
      <div className={cx(styles.mediaFrame, styles.simulationFrame)}>
        {backgroundSource && <Thumbnail media={bannerMedia} className={styles.simulationGlow} defaultImage={defaultBackground} position={position} crop={crop} />}
        <div className={styles.frostedOverlay} />
        <Thumbnail media={bannerMedia} className={cx(getCoachingMediaClassName(entityType, callType), styles.simulationMedia)} defaultImage={defaultBackground} position={position} crop={crop} />
      </div>
    );
  }

  if (entityType === "podcast") {
    return (
      <div className={cx(styles.mediaFrame, styles.podcastFrame)}>
        {backgroundSource && <Thumbnail media={bannerMedia} className={styles.podcastBackground} defaultImage={defaultBackground} position={position} crop={crop} />}
        <div className={styles.podcastOverlay} />
        <Thumbnail media={bannerMedia} className={cx(getCoachingMediaClassName(entityType), styles.simulationMedia)} defaultImage={defaultBackground} position={position} crop={crop} />
      </div>
    );
  }

  return (
    <Thumbnail
      media={bannerMedia}
      className={getCoachingMediaClassName(entityType)}
      defaultImage={defaultBackground}
      position={position}
      crop={crop}
    />
  );
};

interface CoachingPromotedContentCardProps {
  id: string;
  icon: Media;
  name?: string;
  description?: string;
  item?: any;
}

// Main contentCard component
export function CoachingPromotedContentCard({
  icon,
  name,
  description,
  item
}: CoachingPromotedContentCardProps) {

  const course = _.get(item, "course", null);
  const entityType = _.get(item, "courseType", "").toLowerCase();
  const background = getDefaultCoachingBackground(entityType);
  const bannerMedia = _.get(course, "banner.background") || icon;

  const contentIcon: Media = bannerMedia || {
    id: "",
    type: 'image',
    full: background,
    thumbnail: background
  };

  return (
    <div className={cx(styles.content, styles.coachingContent)}>
      <div className={styles.contentIcon}>
        <CoachingMediaRenderer icon={contentIcon} item={item} />
      </div>
      <div className={cx(styles.contentDetails, styles.coachingContentDetails)}>
        <p className={styles.contentName}>{name}</p>
        <div className={styles.contentDescription} dangerouslySetInnerHTML={{ __html: description || "" }} />
        <Button
          text="explore"
          variant="primary"
          className={cx(styles.button, styles.coachingButton)}
          icon="paperflite-right-arrow"
        />

      </div>
    </div>
  );
}

// Placeholder version of contentCard
export function PlaceholderPromotedContentCard() {
  return (
    <div className={cx(styles.content, styles.block, styles.placeholder)}>
      <div className={cx(styles.contentIcon, styles.block)}></div>
      <div className={cx(styles.contentDetails)}>
        <p className={cx(styles.contentName, styles.block)}></p>
        <p className={cx(styles.contentDescription, styles.block)}></p>
      </div>
    </div>
  );
}
